import PieChartIcon from "@mui/icons-material/PieChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { usePermissions, useTranslate } from "react-admin";
import { Navigate } from "react-router-dom";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AnalyticsFilterBar } from "../analytics/AnalyticsFilterBar";
import { useAnalyticsData } from "../analytics/AnalyticsProvider";
import { useAnalyticsFilters } from "../analytics/useAnalyticsFilters";
import {
  AnalyticsGroupBy,
  CHART_PALETTE,
  DistributionRow,
  formatAnalyticsGroupKey,
  formatDateLabel,
  generateKeys,
  getAnalyticsGroupKey,
  Granularity,
  truncDate,
} from "../analytics/utils";
import { ResourceName } from "../constants/resources";
import { space } from "../theme/designTokens";

enum ChartType {
  Line = "line",
  Pie = "pie",
}

const PERCENTAGE_FORMAT = new Intl.NumberFormat("de-DE", { style: "percent", maximumFractionDigits: 1 });

const isUnknownDistributionLabel = (label: string) => label.trim().toLowerCase() === "unknown";

const DistributionLineChart = ({
  data,
  labels,
  granularity,
}: {
  data: Record<string, string | number>[];
  labels: string[];
  granularity: Granularity;
}) => {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const handleMouseLeave = useCallback(() => setHoveredLabel(null), []);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 0 }} onMouseLeave={handleMouseLeave}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={v => formatDateLabel(v, granularity)} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip labelFormatter={v => formatDateLabel(String(v), granularity)} />
        <Legend onMouseEnter={e => setHoveredLabel(e.dataKey as string)} onMouseLeave={handleMouseLeave} />
        {labels.map((label, i) => (
          <Line
            key={label}
            type="monotone"
            dataKey={label}
            name={label}
            stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
            strokeWidth={hoveredLabel === label ? 3 : 2}
            strokeOpacity={hoveredLabel === null || hoveredLabel === label ? 1 : 0.15}
            dot={false}
            isAnimationActive={false}
            onMouseEnter={() => setHoveredLabel(label)}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: "pointer" }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

const DistributionTrend = ({
  rows,
  dimension,
  dateFrom,
  dateTo,
  granularity,
  groupBy,
  chartType,
  onChartTypeChange,
  showTable = false,
}: {
  rows: DistributionRow[];
  dimension: string;
  dateFrom: string;
  dateTo: string;
  granularity: Granularity;
  groupBy: AnalyticsGroupBy;
  chartType: ChartType;
  onChartTypeChange: (type: ChartType) => void;
  showTable?: boolean;
}) => {
  const t = useTranslate();
  const filtered = rows.filter(r => r.dimension === dimension).filter(r => !isUnknownDistributionLabel(r.label));
  if (filtered.length === 0) return null;

  const groupKeys = Array.from(new Set(filtered.map(r => getAnalyticsGroupKey(r.appId, groupBy)))).sort();

  return (
    <>
      {groupKeys.map(groupKey => {
        const groupRows = filtered.filter(r => getAnalyticsGroupKey(r.appId, groupBy) === groupKey);
        const byKey = new Map<string, Record<string, number>>();
        for (const r of groupRows) {
          const key = truncDate(r.date, granularity);
          const entry = byKey.get(key) ?? {};
          entry[r.label] = (entry[r.label] ?? 0) + r.count;
          byKey.set(key, entry);
        }
        const dateKeys = generateKeys(dateFrom, dateTo, granularity);
        const data = dateKeys.map(date => ({
          date,
          ...(byKey.get(date) ?? {}),
        }));

        const labels = Array.from(new Set(groupRows.map(r => r.label))).sort();

        const labelTotals = labels
          .map(label => ({
            name: label,
            value: groupRows.filter(r => r.label === label).reduce((sum, r) => sum + r.count, 0),
          }))
          .sort((a, b) => b.value - a.value);

        const pieTotal = labelTotals.reduce((sum, l) => sum + l.value, 0);

        const labelAverages = showTable
          ? labels
              .map(label => {
                const values = dateKeys.map(dk => byKey.get(dk)?.[label] ?? 0);
                const nonZero = values.filter(v => v > 0);
                return {
                  label,
                  avg: nonZero.length > 0 ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length : 0,
                };
              })
              .sort((a, b) => b.avg - a.avg)
          : [];

        const sortedLabels = showTable ? labelAverages.map(la => la.label) : labels;

        return (
          <Box key={groupKey} sx={{ mb: space.lg }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: space.sm, mb: 1 }}>
              <Typography variant="h6">
                {t(`analytics.dimension.${dimension}`)} — {formatAnalyticsGroupKey(groupKey, groupBy)}
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={chartType}
                exclusive
                onChange={(_, v: ChartType | null) => {
                  if (v !== null) onChartTypeChange(v);
                }}
              >
                <ToggleButton value={ChartType.Pie}>
                  <PieChartIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value={ChartType.Line}>
                  <ShowChartIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {chartType === ChartType.Pie ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={labelTotals}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label={({ name, value }) => `${name} (${PERCENTAGE_FORMAT.format(value / pieTotal)})`}
                  >
                    {labelTotals.map((entry, i) => (
                      <Cell key={entry.name} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <DistributionLineChart data={data} labels={sortedLabels} granularity={granularity} />
            )}
            {showTable && (
              <TableContainer sx={{ maxHeight: 400, mt: 1, overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 3 }}
                      >
                        {t(`analytics.dimension.${dimension}`)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {t("analytics.columns.average")}
                      </TableCell>
                      {dateKeys.map(dk => (
                        <TableCell key={dk} align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {formatDateLabel(dk, granularity)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {labelAverages.map(({ label, avg }, i) => (
                      <TableRow key={label}>
                        <TableCell
                          sx={{ position: "sticky", left: 0, bgcolor: "background.paper", whiteSpace: "nowrap" }}
                        >
                          <Box
                            sx={{
                              display: "inline-block",
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: CHART_PALETTE[i % CHART_PALETTE.length],
                              mr: 1,
                              verticalAlign: "middle",
                            }}
                          />
                          {label}
                        </TableCell>
                        <TableCell align="right">{avg.toFixed(1)}</TableCell>
                        {dateKeys.map(dk => (
                          <TableCell key={dk} align="right">
                            {byKey.get(dk)?.[label] ?? 0}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      })}
    </>
  );
};

const PlatformSummary = ({ rows, groupBy }: { rows: DistributionRow[]; groupBy: AnalyticsGroupBy }) => {
  const t = useTranslate();
  const filtered = rows.filter(r => r.dimension === "platform").filter(r => !isUnknownDistributionLabel(r.label));
  if (filtered.length === 0) return null;

  const groupKeys = Array.from(new Set(filtered.map(r => getAnalyticsGroupKey(r.appId, groupBy)))).sort();

  return (
    <>
      {groupKeys.map(groupKey => {
        const groupRows = filtered.filter(r => getAnalyticsGroupKey(r.appId, groupBy) === groupKey);
        const totals = new Map<string, number>();
        for (const r of groupRows) {
          totals.set(r.label, (totals.get(r.label) ?? 0) + r.count);
        }
        const total = Array.from(totals.values()).reduce((a, b) => a + b, 0);

        return (
          <Box key={groupKey} sx={{ mb: space.lg }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t("analytics.dimension.platform")} — {formatAnalyticsGroupKey(groupKey, groupBy)}
            </Typography>
            <Typography variant="body1">
              {Array.from(totals.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([label, count]) => `${label} ${PERCENTAGE_FORMAT.format(count / total)}`)
                .join(" · ")}
            </Typography>
          </Box>
        );
      })}
    </>
  );
};

export const AnalyticsDistributions = () => {
  const { permissions, isPending } = usePermissions();
  const t = useTranslate();
  const { distRows, loading, error } = useAnalyticsData();
  const filters = useAnalyticsFilters();
  const [groupBy, setGroupBy] = useState<AnalyticsGroupBy>(AnalyticsGroupBy.AppId);
  const [chartType, setChartType] = useState(ChartType.Pie);

  const filteredDistributions = useMemo(
    () =>
      distRows.filter(r => r.date.slice(0, 10) >= filters.deferredFrom && r.date.slice(0, 10) <= filters.deferredTo),
    [distRows, filters.deferredFrom, filters.deferredTo]
  );

  if (!isPending && !permissions?.includes(ResourceName.Analytics)) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ p: 4 }}>
      <AnalyticsFilterBar
        interval={filters.interval}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        granularity={filters.granularity}
        granularityDisabled={chartType === ChartType.Pie}
        onIntervalChange={filters.handleIntervalChange}
        onDateFromChange={filters.setDateFrom}
        onDateToChange={filters.setDateTo}
        onGranularityChange={filters.setGranularity}
      />
      <FormControl size="small" sx={{ minWidth: 180, mt: space.sm, mb: space.lg }}>
        <InputLabel id="analytics-distributions-group-by-label">{t("analytics.groupBy")}</InputLabel>
        <Select
          labelId="analytics-distributions-group-by-label"
          value={groupBy}
          label={t("analytics.groupBy")}
          onChange={(event: SelectChangeEvent<AnalyticsGroupBy>) => setGroupBy(event.target.value as AnalyticsGroupBy)}
        >
          <MenuItem value={AnalyticsGroupBy.AppId}>{t("analytics.groupByOption.appId")}</MenuItem>
          <MenuItem value={AnalyticsGroupBy.Flavour}>{t("analytics.groupByOption.flavour")}</MenuItem>
        </Select>
      </FormControl>

      {loading && <CircularProgress sx={{ my: space.lg }} />}
      {error && (
        <Typography color="error" sx={{ mb: space.md }}>
          {t("analytics.loadError")}
        </Typography>
      )}

      {filters.deferredFrom && filters.deferredTo && filteredDistributions.length > 0 && (
        <>
          <DistributionTrend
            rows={filteredDistributions}
            dimension="app_version"
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
            groupBy={groupBy}
            chartType={chartType}
            onChartTypeChange={setChartType}
            showTable
          />
          <DistributionTrend
            rows={filteredDistributions}
            dimension="os_version"
            dateFrom={filters.deferredFrom}
            dateTo={filters.deferredTo}
            granularity={filters.granularity}
            groupBy={groupBy}
            chartType={chartType}
            onChartTypeChange={setChartType}
            showTable
          />
          <PlatformSummary rows={filteredDistributions} groupBy={groupBy} />
        </>
      )}
    </Box>
  );
};
